import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { api, ApiError } from "@/api/client";
import type {
  Candle,
  OHLCMetadata,
  OHLCRequest,
  OHLCResponse,
  TickProfileBin,
} from "@/api/types";
import type { RootState } from "../store";
import { DEFAULT_TAB_ID, addTab, removeTab } from "./tabsSlice";
import { hydrateWorkspace } from "./workspacesSlice";
import { isHtfTimeframe } from "@/utils/timeframe";

interface MonthRef {
  year: number;
  month: number;
}

/** Per-tab OHLC state. Owned by tabId so async thunks that resolve
 *  after a tab switch still write to the correct tab. */
export interface OhlcTabState {
  candles: Candle[];
  metadata: OHLCMetadata | null;
  tickProfile: TickProfileBin[];
  filesLoaded: string[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;

  /** The (symbol, timeframe) a fetchOhlc just FAILED for. While this
   *  matches the tab's current symbol/TF, the Chart's fetch effect must
   *  NOT auto-redispatch — otherwise the loading→failed transition
   *  re-runs the effect and retries in a tight loop, hammering the
   *  backend. Cleared on a new attempt / success / manual retry; a
   *  symbol or TF change naturally stops matching and re-fetches. */
  failedFor: { symbol: string; timeframe: string } | null;

  // Lazy-load bookkeeping (intraday only).
  oldestDataTime: number | null;
  nextLoadMonth: MonthRef | null;
  isLoadingMore: boolean;
  noMoreData: boolean;
  /** A loadMore just failed with a REAL error (5xx/network). Blocks
   *  further lazy-load dispatches so a broken backend isn't hammered;
   *  cleared by a successful fetchOhlc or a manual retry. */
  loadMoreFailed: boolean;
  emptyStreakCount: number;

  // Increments on every successful prepend (lazy load that returned new
  // candles). The Chart component watches this to know when to shift the
  // visible logical range so the user's pan position is preserved.
  prependSerial: number;
  lastPrependCount: number;

  // Increments on every successful fetchOhlc.fulfilled. Lets the Chart
  // distinguish a fresh load (fitContent + 20% extension) from a
  // chart-type change with the same data (preserve current view).
  fetchSerial: number;

  /** The (symbol, timeframe) tuple this tab's `candles` were fetched
   *  for. The Chart effect compares against the tab's current
   *  symbol/TF to decide whether to refetch — without this, switching
   *  to a tab that already has its data cached would still trigger a
   *  network round-trip on every visit. Set on fetchOhlc.fulfilled,
   *  cleared by clearOhlc. */
  loadedFor: { symbol: string; timeframe: string } | null;

  /** Unix ms timestamp of the last successful fetchOhlc. Used by the
   *  stale-data check in Chart.tsx: when the browser tab resumes after
   *  a long absence the chart re-fetches so the gap of missed candles
   *  is filled. 0 = never fetched. */
  lastFetchAt: number;
}

interface OhlcState {
  byTab: Record<string, OhlcTabState>;
}

function createDefaultOhlcTabState(): OhlcTabState {
  return {
    candles: [],
    metadata: null,
    tickProfile: [],
    filesLoaded: [],
    status: "idle",
    error: null,
    failedFor: null,

    oldestDataTime: null,
    nextLoadMonth: null,
    isLoadingMore: false,
    noMoreData: false,
    loadMoreFailed: false,
    emptyStreakCount: 0,

    prependSerial: 0,
    lastPrependCount: 0,
    fetchSerial: 0,
    loadedFor: null,
    lastFetchAt: 0,
  };
}

const initialState: OhlcState = {
  byTab: { [DEFAULT_TAB_ID]: createDefaultOhlcTabState() },
};

function getTab(state: OhlcState, tabId: string): OhlcTabState {
  let t = state.byTab[tabId];
  if (!t) {
    t = createDefaultOhlcTabState();
    state.byTab[tabId] = t;
  }
  return t;
}

function monthFromTimestamp(ts: number): MonthRef {
  const d = new Date(ts * 1000);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
}

function decrementMonth({ year, month }: MonthRef): MonthRef {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}

export interface FetchOhlcArg {
  tabId: string;
  request: OHLCRequest;
}

export const fetchOhlc = createAsyncThunk<
  { response: OHLCResponse; tabId: string },
  FetchOhlcArg
>("ohlc/fetch", async (arg) => {
  const response = await api.loadOhlc(arg.request);
  return { response, tabId: arg.tabId };
});

export const loadMoreOhlc = createAsyncThunk<
  { response: OHLCResponse | null; targetMonth: MonthRef | null; tabId: string },
  { tabId: string },
  { state: RootState }
>(
  "ohlc/loadMore",
  async ({ tabId }, { getState }) => {
    const root = getState();
    const tabUi = root.ui.byTab[tabId];
    const tabOhlc = root.ohlc.byTab[tabId];
    const target = tabOhlc!.nextLoadMonth!; // condition guarantees this
    try {
      const response = await api.loadOhlc({
        symbol: tabUi.symbol,
        timeframe: tabUi.timeframe,
        year: target.year,
        month: target.month,
        max_files: 31,
      });
      return { response, targetMonth: target, tabId };
    } catch (err) {
      // 404 = no data file for that month: the expected miss path —
      // treat as empty and advance to the previous month.
      if (err instanceof ApiError && err.status === 404) {
        return { response: null, targetMonth: target, tabId };
      }
      // Any other error (5xx / network) is a REAL failure — re-throw so
      // the rejected handler latches it and stops, instead of pretending
      // it's no-data and hammering 12 months against a broken backend.
      throw err;
    }
  },
  {
    // Runs BEFORE the pending action, so our isLoadingMore guard works.
    condition: ({ tabId }, { getState }) => {
      const root = getState();
      const tabUi = root.ui.byTab[tabId];
      const tabOhlc = root.ohlc.byTab[tabId];
      if (!tabUi || !tabOhlc) return false;
      if (!tabOhlc.nextLoadMonth) return false;
      if (tabOhlc.isLoadingMore) return false;
      if (tabOhlc.noMoreData) return false;
      if (tabOhlc.loadMoreFailed) return false;
      if (isHtfTimeframe(tabUi.timeframe)) return false;
      return true;
    },
  },
);

const ohlcSlice = createSlice({
  name: "ohlc",
  initialState,
  reducers: {
    clearOhlc(state, action: PayloadAction<{ tabId: string }>) {
      state.byTab[action.payload.tabId] = createDefaultOhlcTabState();
    },
    /** Manual retry after a failed load: clear the failure latch so the
     *  Chart fetch effect re-dispatches the (same) request once. */
    retryOhlc(state, action: PayloadAction<{ tabId: string }>) {
      const t = getTab(state, action.payload.tabId);
      t.failedFor = null;
      t.loadMoreFailed = false;
      t.error = null;
      t.status = "idle";
    },
    /** Stale-data refresh: clear loadedFor + status so the Chart fetch
     *  effect re-fetches, but KEEP candles so the chart doesn't go blank
     *  while the new request is in-flight. Dispatched when the browser
     *  tab resumes after a long absence (see Chart.tsx visibilitychange). */
    forceRefreshOhlc(state, action: PayloadAction<{ tabId: string }>) {
      const t = getTab(state, action.payload.tabId);
      t.loadedFor = null;
      t.failedFor = null;
      t.loadMoreFailed = false;
      t.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addTab, (state, action) => {
        if (!state.byTab[action.payload.id]) {
          state.byTab[action.payload.id] = createDefaultOhlcTabState();
        }
      })
      .addCase(removeTab, (state, action) => {
        delete state.byTab[action.payload];
      })
      .addCase(hydrateWorkspace, (state, action) => {
        // Workspace doesn't carry candles — they're refetched on the
        // Chart's first paint. BUT: keep existing ohlc state for any
        // tab whose symbol+timeframe is unchanged in the new payload.
        // Critical for OCC-triggered re-hydrates (= the other browser
        // session edited an unrelated field, our save 409'd, we
        // refetched the same workspace) — without this, every refetch
        // wiped loadedFor → forced fetchOhlc → bumped fetchSerial →
        // the data layout effect treated it as a fresh load and
        // either restored the stale page-hide-saved viewport or fell
        // back to fitContent. Result: the user's zoom / pan reverted
        // on every cross-session race, even though nothing in the
        // workspace actually required a chart reset.
        //
        // Tabs whose symbol/TF DID change (initial boot, real
        // workspace switch, etc.) still reset — they need a fresh
        // fetch for the new (symbol, TF) anyway.
        const next: Record<string, OhlcTabState> = {};
        for (const [tabId, tabState] of Object.entries(
          action.payload.byTab,
        )) {
          const existing = state.byTab[tabId];
          const sameLoadedFor =
            existing?.loadedFor != null &&
            existing.loadedFor.symbol === tabState.symbol &&
            existing.loadedFor.timeframe === tabState.timeframe;
          next[tabId] = sameLoadedFor
            ? existing
            : createDefaultOhlcTabState();
        }
        state.byTab = next;
      })

      .addCase(fetchOhlc.pending, (state, action) => {
        const t = getTab(state, action.meta.arg.tabId);
        t.status = "loading";
        t.error = null;
        t.failedFor = null;
      })
      .addCase(fetchOhlc.fulfilled, (state, action) => {
        const { response, tabId } = action.payload;
        const t = getTab(state, tabId);
        t.status = "succeeded";
        t.candles = response.candles;
        t.metadata = response.metadata;
        t.tickProfile = response.tick_profile;
        t.filesLoaded = response.files_loaded;

        const oldest = response.candles[0]?.time ?? null;
        t.oldestDataTime = oldest;
        t.nextLoadMonth = oldest ? monthFromTimestamp(oldest) : null;
        t.isLoadingMore = false;
        t.noMoreData = false;
        t.loadMoreFailed = false;
        t.emptyStreakCount = 0;
        t.lastPrependCount = 0;
        t.fetchSerial += 1;
        t.failedFor = null;
        t.loadedFor = {
          symbol: action.meta.arg.request.symbol,
          timeframe: action.meta.arg.request.timeframe,
        };
        t.lastFetchAt = Date.now();
      })
      .addCase(fetchOhlc.rejected, (state, action) => {
        const t = getTab(state, action.meta.arg.tabId);
        t.status = "failed";
        t.error = action.error.message ?? "Failed to load OHLC";
        // Latch the failed (symbol, TF) so the Chart effect stops
        // auto-retrying it in a loop. Retry is manual / on symbol-TF change.
        t.failedFor = {
          symbol: action.meta.arg.request.symbol,
          timeframe: action.meta.arg.request.timeframe,
        };
      })

      .addCase(loadMoreOhlc.pending, (state, action) => {
        getTab(state, action.meta.arg.tabId).isLoadingMore = true;
      })
      .addCase(loadMoreOhlc.fulfilled, (state, action) => {
        const { response, targetMonth, tabId } = action.payload;
        const t = getTab(state, tabId);
        t.isLoadingMore = false;
        if (!targetMonth) return;

        if (response && response.candles.length > 0) {
          const existingTimes = new Set(t.candles.map((c) => c.time));
          const newCandles = response.candles.filter(
            (c) => !existingTimes.has(c.time),
          );

          if (newCandles.length > 0) {
            const merged = [...newCandles, ...t.candles];
            merged.sort((a, b) => a.time - b.time);
            t.candles = merged;
            t.oldestDataTime = merged[0].time;
            t.lastPrependCount = newCandles.length;
            t.prependSerial += 1;
            t.emptyStreakCount = 0;
          }
        } else {
          t.emptyStreakCount += 1;
          if (t.emptyStreakCount >= 12) {
            t.noMoreData = true;
          }
        }

        t.nextLoadMonth = decrementMonth(targetMonth);
      })
      .addCase(loadMoreOhlc.rejected, (state, action) => {
        const t = getTab(state, action.meta.arg.tabId);
        t.isLoadingMore = false;
        // Real error (re-thrown from the thunk) — latch off lazy-load so
        // we don't keep re-dispatching against a broken backend.
        t.loadMoreFailed = true;
      });
  },
});

export const { clearOhlc, retryOhlc, forceRefreshOhlc } = ohlcSlice.actions;
export default ohlcSlice.reducer;
